import React, { Fragment } from 'react';
import { Menu, Transition } from '@headlessui/react';
import { UserIcon, ArrowLeftOnRectangleIcon, Cog6ToothIcon, CurrencyDollarIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

interface UserMenuProps {
  userName?: string;
  userRole?: string;
  onProfileClick: () => void;
  onCoinsClick: () => void;
  onLogout: () => void;
  collapsed?: boolean;
}

const UserMenu: React.FC<UserMenuProps> = ({
  userName,
  userRole,
  onProfileClick,
  onCoinsClick,
  onLogout,
  collapsed = false,
}) => {
  const { t } = useTranslation(['nav']);
  return (
    <Menu as="div" className="relative">
      <Menu.Button
        className={`w-full flex items-center rounded-lg  cursor-pointer transition-all duration-200 border border-transparent hover:border-gray-600 active:scale-95 active:bg-gray-600 ${collapsed ? 'p-2 justify-center' : 'p-3 gap-3'
          }`}
      >
        <div className="w-9 h-9 bg-gradient-to-br from-gray-500 to-gray-600 rounded-full flex items-center justify-center flex-shrink-0">
          <UserIcon className="w-5 h-5 text-white" />
        </div>
        {!collapsed && (
          <div className="flex-1 min-w-0 text-left">
            <div className="text-sm font-medium text-gray-50 truncate">
              {userName || t('nav:userMenu.defaultName')}
            </div>
            <div className="text-xs text-gray-400">
              {userRole === 'admin' ? t('nav:userMenu.roleAdmin') : t('nav:userMenu.roleUser')}
            </div>
          </div>
        )}
      </Menu.Button>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute bottom-full left-0 mb-2 w-48 origin-bottom bg-bg-elevated rounded-lg shadow-lg ring-1 ring-border-subtle ring-opacity-50 focus:outline-none py-1 z-50">
          <Menu.Item>
            {({ active }) => (
              <button
                onClick={onProfileClick}
                className={`${active ? 'bg-bg-surface' : ''
                  } group flex w-full items-center gap-3 px-4 py-2 text-sm text-gray-200`}
              >
                <UserIcon className="w-4 h-4 text-gray-400" />
                {t('nav:userMenu.profile')}
              </button>
            )}
          </Menu.Item>
          <Menu.Item>
            {({ active }) => (
              <button
                onClick={onCoinsClick}
                className={`${active ? 'bg-bg-surface' : ''
                  } group flex w-full items-center gap-3 px-4 py-2 text-sm text-gray-200`}
              >
                <CurrencyDollarIcon className="w-4 h-4 text-gray-400" />
                {t('nav:userMenu.myCoins')}
              </button>
            )}
          </Menu.Item>
          <Menu.Item>
            {({ active }) => (
              <button
                className={`${active ? 'bg-bg-surface' : ''
                  } group flex w-full items-center gap-3 px-4 py-2 text-sm text-gray-200`}
              >
                <Cog6ToothIcon className="w-4 h-4 text-gray-400" />
                {t('nav:userMenu.settings')}
              </button>
            )}
          </Menu.Item>
          <div className="border-t border-border-subtle my-1" />
          <Menu.Item>
            {({ active }) => (
              <button
                onClick={onLogout}
                className={`${active ? 'bg-bg-surface' : ''
                  } group flex w-full items-center gap-3 px-4 py-2 text-sm text-red-400`}
              >
                <ArrowLeftOnRectangleIcon className="w-4 h-4" />
                {t('nav:userMenu.logout')}
              </button>
            )}
          </Menu.Item>
        </Menu.Items>
      </Transition>
    </Menu>
  );
};

export default UserMenu;
